import { useRef, useState } from "react";
import api, { fmtNZD } from "@/lib/api";
import Field from "@/components/Field";
import StageSelect from "@/components/StageSelect";

/**
 * Merchant CSV importer modal.
 *
 * Flow:
 *   1. Pick file → POST /estimate/import-preview
 *   2. Review detected header + mapping. Any unmatched canonical field becomes a
 *      dropdown so Luke can pick the right column from a preview of the first 5 rows.
 *   3. Map each merchant Stage label to a Hub stage (pre-filled, Unassigned allowed with warning).
 *   4. Ex/Inc GST toggle. Commit → POST /estimate/import-commit.
 *   5. After commit: show per-stage subtotals + Undo button → /estimate/import-undo.
 */
const CANONICAL_FIELDS = [
  ["code", "Item code / SKU"],
  ["description", "Description"],
  ["unit", "Unit / UOM"],
  ["qty", "Quantity"],
  ["rate", "Unit price / Sell price"],
  ["total", "Line total"],
  ["stage", "Stage / Category"],
  ["notes", "Usage / Notes"],
];

export default function MerchantImporterModal({ projectId, onClose, onImported }) {
  const [step, setStep] = useState("pick");     // pick | preview | committed | failed
  const [file, setFile] = useState(null);
  const [merchant, setMerchant] = useState("Auto");
  const [category, setCategory] = useState("Materials");
  const [preview, setPreview] = useState(null); // backend preview payload
  const [stageMap, setStageMap] = useState({}); // merchant_label → hub_stage_name
  const [mappingOverride, setMappingOverride] = useState({}); // canonical → col index
  const [gstInclusive, setGstInclusive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [commit, setCommit] = useState(null);
  const fileRef = useRef(null);

  async function doPreview(e) {
    e?.preventDefault();
    const f = fileRef.current?.files?.[0]; if (!f) { setErr("Pick a CSV file first"); return; }
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("merchant", merchant);
    fd.append("category", category);
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate/import-preview`, fd,
                                       { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setFile(f);
      // Seed stage map from backend suggestions
      const sm = {};
      (data.merchant_label_mapping || []).forEach(x => { if (x.suggested_hub_stage) sm[x.merchant_label] = x.suggested_hub_stage; });
      setStageMap(sm);
      setMappingOverride({ ...(data.detected?.mapping || {}) });
      setStep("preview");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview failed"); setStep("failed");
    } finally { setBusy(false); }
  }

  async function doCommit() {
    if (!preview) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate/import-commit`, {
        session_id: preview.session_id, category, gst_inclusive: gstInclusive,
        stage_map: stageMap,
        mapping_override: mappingOverride,
      });
      setCommit(data); setStep("committed");
      onImported?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Commit failed");
    } finally { setBusy(false); }
  }

  async function doUndo() {
    if (!commit?.undo_version_id) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/projects/${projectId}/estimate/import-undo`, { version_id: commit.undo_version_id });
      setCommit(null); setStep("pick"); setPreview(null);
      setStageMap({}); setMappingOverride({});
      if (fileRef.current) fileRef.current.value = "";
      onImported?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Undo failed"); }
    finally { setBusy(false); }
  }

  const unresolved = CANONICAL_FIELDS.filter(([k]) => k !== "stage" && k !== "notes")
    .filter(([k]) => mappingOverride[k] === undefined || mappingOverride[k] === null);
  const columnLabels = preview?.detected?.header_labels || [];
  const previewRows5 = preview?.preview_rows || [];
  const hubStages = preview?.hub_stages || [];
  const projectHasStages = preview?.project_has_stages;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="merchant-importer-modal">
      <div className="card max-w-5xl w-full max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-silver text-lg">Import merchant quote (CSV)</h3>
          <button className="btn-ghost text-sm" onClick={onClose} data-testid="btn-mi-close">Close</button>
        </div>
        {err && <div className="text-red-500 text-sm mb-2" data-testid="mi-error">{err}</div>}

        {step === "pick" && (
          <form onSubmit={doPreview} className="space-y-3" data-testid="mi-step-pick">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Field label="Merchant hint" hint="Only used for the restore-point name">
                <select className="input" value={merchant} onChange={e=>setMerchant(e.target.value)} data-testid="mi-merchant">
                  <option>Auto</option><option>ITM</option><option>PlaceMakers</option>
                  <option>Mitre 10</option><option>Carters</option><option>Bunnings</option><option>Generic</option>
                </select>
              </Field>
              <Field label="Target category" hint="Grouping label in the Materials pane">
                <input className="input" value={category} onChange={e=>setCategory(e.target.value)} data-testid="mi-category"/>
              </Field>
              <Field label="CSV file" required>
                <input ref={fileRef} type="file" accept=".csv" className="text-sm text-white" data-testid="mi-file"/>
              </Field>
            </div>
            <button className="btn-silver" disabled={busy} data-testid="btn-mi-preview">
              {busy ? "Reading…" : "Preview import"}
            </button>
          </form>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4" data-testid="mi-step-preview">
            {/* Header detection */}
            <div className="card">
              <div className="text-silver text-sm mb-1">Detected header</div>
              <div className="text-xs opacity-70">Row <b>{preview.detected.header_row}</b> · {columnLabels.length} columns · {file?.name}</div>
              <div className="mt-2 flex flex-wrap gap-1 text-xs">
                {columnLabels.map((lbl, i) => {
                  const matched = Object.entries(mappingOverride).find(([, idx]) => idx === i);
                  return (
                    <span key={i} className={`px-2 py-0.5 border rounded ${matched ? "bg-silver/10 border-silver/50 text-silver" : "border-neutral-800 opacity-60"}`}>
                      {lbl}{matched ? ` → ${matched[0]}` : ""}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Column mapping overrides */}
            {unresolved.length > 0 && (
              <div className="card border border-amber-500/40 bg-amber-500/5" data-testid="mi-column-mapping">
                <div className="text-amber-200 text-sm mb-2">
                  {unresolved.length} column{unresolved.length===1?"":"s"} couldn't be auto-matched. Pick the right CSV column for each:
                </div>
                <div className="space-y-2">
                  {unresolved.map(([k, lbl]) => (
                    <div key={k} className="flex items-center gap-2">
                      <div className="text-xs text-silver w-48 shrink-0">{lbl}</div>
                      <select className="input flex-1" value={mappingOverride[k] ?? ""}
                              onChange={e => setMappingOverride({ ...mappingOverride, [k]: e.target.value === "" ? undefined : parseInt(e.target.value) })}
                              data-testid={`mi-map-${k}`}>
                        <option value="">— pick column —</option>
                        {columnLabels.map((lbl2, i) => <option key={i} value={i}>{lbl2}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* First 5 rows preview */}
            <div className="card">
              <div className="text-silver text-sm mb-2">First 5 rows ({preview.line_count} total · {preview.heading_count} section headings skipped)</div>
              <div className="overflow-x-auto">
                <table className="table text-xs min-w-full">
                  <thead><tr><th>Code</th><th>Description</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Total</th><th>Stage</th></tr></thead>
                  <tbody>
                    {previewRows5.map((r, i) => (
                      <tr key={i}>
                        <td>{r.code || "—"}</td>
                        <td>{r.desc}</td>
                        <td className="text-right">{r.qty}</td>
                        <td>{r.unit}</td>
                        <td className="text-right">{fmtNZD(r.rate)}</td>
                        <td className="text-right">{fmtNZD(r.total)}</td>
                        <td className="opacity-70">{r.stage_label || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per-merchant-stage subtotals */}
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-silver text-sm">Per-stage preview</div>
                <div className="text-silver text-sm" data-testid="mi-grand-total">
                  Grand total ex GST: <b>{fmtNZD(preview.grand_total_ex_gst)}</b>
                  {Math.abs(preview.cents_mismatch) > 0.01 && (
                    <span className="opacity-60 text-xs ml-2">({preview.cents_mismatch > 0 ? "+" : ""}{preview.cents_mismatch.toFixed(2)} vs qty×rate)</span>
                  )}
                </div>
              </div>
              {!projectHasStages ? (
                <div className="border border-amber-500/50 bg-amber-500/10 p-2 rounded text-sm text-amber-200" data-testid="mi-needs-template">
                  This project has no stages yet. Open the <b>Tasks</b> tab and apply a build template first — the importer will auto-map once stages exist.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table text-sm min-w-full">
                    <thead><tr><th>Merchant stage</th><th>Lines</th><th className="text-right">Subtotal ex GST</th><th>Hub stage</th></tr></thead>
                    <tbody>
                      {Object.entries(preview.per_merchant_stage_subtotals || {}).map(([label, s]) => (
                        <tr key={label} data-testid={`mi-stage-${label}`}>
                          <td className="text-silver">{label}</td>
                          <td>{s.count}</td>
                          <td className="text-right">{fmtNZD(s.total)}</td>
                          <td>
                            <StageSelect
                              projectId={projectId}
                              value={(hubStages.find(h=>h.name===stageMap[label])||{}).id || ""}
                              stages={hubStages}
                              onStagesChanged={async () => {
                                const { data } = await api.get(`/projects/${projectId}/stages`);
                                setPreview(p => ({ ...p, hub_stages: data }));
                              }}
                              onChange={(sid) => {
                                const name = (hubStages.find(h => h.id === sid) || {}).name || "";
                                setStageMap({ ...stageMap, [label]: name });
                              }}
                              data-testid={`mi-stage-pick-${label}`}/>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {Object.entries(preview.per_merchant_stage_subtotals || {}).some(([lbl]) => !stageMap[lbl]) && (
                <div className="mt-2 text-xs text-amber-300" data-testid="mi-unassigned-warn">
                  Rows under unmapped merchant stages will be imported as <b>Unassigned</b>. Map them to a Hub stage above, or import as-is and fix later from the Materials tab.
                </div>
              )}
            </div>

            {/* Warnings */}
            {(preview.warnings || []).length > 0 && (
              <div className="card">
                <div className="text-silver text-sm mb-1">{preview.warnings.length} warning{preview.warnings.length===1?"":"s"}</div>
                <ul className="text-xs opacity-80 max-h-32 overflow-auto">
                  {preview.warnings.slice(0, 50).map((w, i) => (
                    <li key={i}>• Row {w.row}: {w.reason}{w.desc ? ` — ${w.desc}` : ""}</li>
                  ))}
                  {preview.warnings.length > 50 && <li>…and {preview.warnings.length - 50} more</li>}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm flex items-center gap-2" data-testid="mi-gst-label">
                <input type="checkbox" checked={gstInclusive} onChange={e=>setGstInclusive(e.target.checked)} data-testid="mi-gst"/>
                Prices in this file are INC GST (divide by 1.15 on import)
              </label>
              <div className="flex-1"/>
              <button className="btn-ghost" onClick={() => setStep("pick")} data-testid="btn-mi-back">← Back</button>
              <button className="btn-silver" onClick={doCommit} disabled={busy || unresolved.length > 0} data-testid="btn-mi-commit">
                {busy ? "Importing…" : `Commit ${preview.line_count} lines`}
              </button>
            </div>
          </div>
        )}

        {step === "committed" && commit && (
          <div className="space-y-3" data-testid="mi-step-committed">
            <div className="card border border-silver/40">
              <div className="text-silver text-sm mb-1">Imported ✓</div>
              <div className="text-sm">
                <b>{commit.added}</b> lines added · <b>{fmtNZD(commit.added_total_ex_gst)}</b> ex GST
                {commit.unassigned_lines > 0 && (
                  <span className="text-amber-300 ml-2">· {commit.unassigned_lines} unassigned</span>
                )}
              </div>
            </div>
            <div className="card">
              <div className="text-silver text-sm mb-2">Per-stage subtotals</div>
              <table className="table">
                <thead><tr><th>Hub stage</th><th>Lines</th><th className="text-right">Subtotal</th></tr></thead>
                <tbody>
                  {Object.entries(commit.per_stage_subtotals || {}).map(([name, s]) => (
                    <tr key={name} data-testid={`mi-committed-${name}`}>
                      <td className={name === "Unassigned" ? "text-amber-300" : "text-silver"}>{name}</td>
                      <td>{s.count}</td>
                      <td className="text-right">{fmtNZD(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <button className="btn-silver" onClick={onClose} data-testid="btn-mi-done">Done</button>
              <button className="btn-ghost text-red-400" onClick={doUndo} disabled={busy} data-testid="btn-mi-undo">
                {busy ? "Undoing…" : "Undo this import"}
              </button>
            </div>
          </div>
        )}

        {step === "failed" && (
          <div className="card border border-red-500/40">
            <div className="text-red-400 text-sm mb-2">Import failed</div>
            <div className="text-sm opacity-80 whitespace-pre-wrap mb-3">{err}</div>
            <button className="btn-silver" onClick={() => { setStep("pick"); setErr(""); }} data-testid="btn-mi-restart">Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}
